/**
 * ManageDrawsRodeoCard
 *
 * One rodeo row on the delegate's Manage Draws list. Collapsed by
 * default — the header shows aggregate counts, the sandbox/live draw
 * mode badge, bulk reset actions, and (when sandbox) an inline
 * "Initialize Draw" CTA so admins can run the ferris wheel without
 * having to drill into an event. Click anywhere on the header to
 * expand the body (conflict panels + per-event rows).
 *
 * The draw-mode fetch happens per-rodeo here rather than in the
 * parent because React Query dedupes on the query key (rodeoId), so
 * mounting many cards is fine — each card is a self-contained unit.
 */

import { Fragment, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  Ban,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
  MapPin,
  Play,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateRange } from '@/utils/formatters';
import { useToast } from '@/contexts/ToastContext';
import {
  useDrawMode,
  useInitializeDraw,
} from '@/api/extensions/useDrawInitialization';
import type { EventDrawStatus } from '@/api/extensions/useDrawListStatus';
import { InitializationHistoryModal } from '@/components/delegate/InitializationHistoryModal';

interface ManageDrawsRodeoCardProps {
  rodeo: {
    id: string;
    name: string | null;
    primary_organization_name?: string | null;
    primary_organization_id?: string | null;
    location?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  };
  events: any[];
  activeEventCount: number;
  enteredTotal: number;
  notDrawnTotal: number;
  cancelledTotal: number;
  draftEventCount: number;
  /** Map of event_id → status; used by the per-event rollup row counts. */
  statuses: Record<string, EventDrawStatus | undefined>;
  /** Bulk reset / reset+run actions, rendered into the header. */
  bulkActions: ReactNode;
  /**
   * Rodeo-header master "show all results" toggle. Floats left in the
   * actions row. Renders nothing until the rodeo has a finalized event,
   * so it's safe to always pass.
   */
  masterResultsToggle?: ReactNode;
  /** Conflict + buddy-group panels, only shown when expanded. */
  expandedPanels: ReactNode;
  /** Per-event rows, only shown when expanded. */
  eventRows: ReactNode;
  /**
   * Controlled expanded state. Lifted into DelegateDrawsPage so the
   * card stays open while the user drills into a draw event and
   * navigates back; local state would reset on unmount. When omitted,
   * the card manages its own state (default collapsed).
   */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  /** Used only when `expanded` is uncontrolled. */
  defaultExpanded?: boolean;
}

export function ManageDrawsRodeoCard({
  rodeo,
  events,
  activeEventCount,
  enteredTotal,
  notDrawnTotal,
  cancelledTotal,
  bulkActions,
  masterResultsToggle,
  expandedPanels,
  eventRows,
  expanded: controlledExpanded,
  onExpandedChange,
  defaultExpanded = false,
}: ManageDrawsRodeoCardProps) {
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : uncontrolledExpanded;
  const setExpanded = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved =
      typeof next === 'function' ? next(expanded) : next;
    if (onExpandedChange) onExpandedChange(resolved);
    if (!isControlled) setUncontrolledExpanded(resolved);
  };
  const { data: drawModeData } = useDrawMode(rodeo.id);
  const { showSuccess, showError } = useToast();
  const initialize = useInitializeDraw();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isSandbox = drawModeData?.draw_mode === 'sandbox';
  const isLive = drawModeData?.draw_mode === 'live';

  const handleInitialize = async () => {
    try {
      const result = await initialize.mutateAsync({ rodeoId: rodeo.id });
      // A membership-less association rotates nobody — say that plainly
      // instead of "rotated 0 members", which reads like a failure.
      const rotatedStr =
        result.member_count === 0
          ? 'This association has no members on the priority list, so there was nothing to rotate.'
          : `Ferris wheel rotated ${result.member_count} member${result.member_count === 1 ? '' : 's'}.`;
      const resetStr =
        result.draft_draws_reset > 0
          ? ` ${result.draft_draws_reset} sandbox draft draw${result.draft_draws_reset === 1 ? ' was' : 's were'} cleared.`
          : '';
      showSuccess(
        'Draw initialized',
        `${rotatedStr} The draw is now live.${resetStr}`,
      );
      setConfirmOpen(false);
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      const friendly =
        e?.code === 'ALREADY_LIVE'
          ? 'This rodeo is already in live mode.'
          : e?.code === 'PARTIALLY_INITIALIZED'
            ? 'A prior attempt left this rodeo in a half-initialized state. Manual cleanup is required.'
            : (e?.message ?? 'Unknown error');
      showError('Initialization failed', friendly);
    }
  };

  const ChevronIcon = expanded ? ChevronDown : ChevronRight;

  return (
    <Fragment>
      <div className="rounded-lg border border-border overflow-hidden">
        {/* Header — clickable to expand. We use a real button for
            keyboard/focus correctness but keep the visual treatment
            of the original gray header band. */}
        <div
          className={`bg-gray-700 dark:bg-gray-800 px-6 py-4 ${
            isSandbox
              ? 'border-l-4 border-l-amber-500 dark:border-l-amber-400'
              : isLive
                ? 'border-l-4 border-l-emerald-500 dark:border-l-emerald-400'
                : ''
          }`}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="flex items-start gap-3 text-left min-w-0 flex-1 group"
              aria-expanded={expanded}
              aria-controls={`rodeo-${rodeo.id}-body`}
            >
              <ChevronIcon className="mt-1 h-5 w-5 flex-shrink-0 text-gray-300 group-hover:text-white transition-colors" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-white">
                    {rodeo.name}
                  </h2>
                  {drawModeData ? (
                    <DrawModeBadge mode={drawModeData.draw_mode} />
                  ) : null}
                </div>
                {rodeo.primary_organization_name && (
                  <p className="text-sm text-gray-300 flex items-center gap-1 mt-0.5">
                    <Building2 className="h-3 w-3" />
                    {rodeo.primary_organization_name}
                  </p>
                )}
                {rodeo.location && (
                  <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3 w-3" />
                    <span className="truncate">{rodeo.location}</span>
                  </p>
                )}
              </div>
            </button>
            <div className="text-right shrink-0 space-y-1">
              <div className="text-sm text-gray-300">
                {activeEventCount} active event
                {activeEventCount !== 1 ? 's' : ''} &bull; {events.length}{' '}
                total
              </div>
              {(rodeo.start_date || rodeo.end_date) && (
                <div className="flex items-center justify-end gap-1 text-xs text-gray-400">
                  <Calendar className="h-3 w-3" />
                  <span>
                    {formatDateRange(rodeo.start_date, rodeo.end_date)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-end gap-3 text-xs flex-wrap">
                <span className="flex items-center gap-1 text-blue-300">
                  <Users className="h-3 w-3" />
                  {enteredTotal} entered
                </span>
                {notDrawnTotal > 0 && (
                  <span className="flex items-center gap-1 text-amber-300 font-medium">
                    <AlertTriangle className="h-3 w-3" />
                    {notDrawnTotal} not drawn
                  </span>
                )}
                {cancelledTotal > 0 && (
                  <span className="flex items-center gap-1 text-rose-300">
                    <Ban className="h-3 w-3" />
                    {cancelledTotal} cancelled
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Actions row: bulk reset/run on the right; when the rodeo
              is in sandbox mode an Initialize Draw CTA surfaces here
              so the delegate doesn't have to drill into an event to
              find it. Once live, the Initialize CTA hides and a
              View Initialization History button takes its place so
              admins can audit prior→new positions without drilling. */}
          <div className="mt-3 flex items-center justify-end gap-2 flex-wrap">
            {masterResultsToggle}
            {isSandbox ? (
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmOpen(true);
                }}
                disabled={initialize.isPending}
                className="bg-amber-700 text-white hover:bg-amber-800 dark:bg-amber-700 dark:hover:bg-amber-600"
              >
                {initialize.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Initializing…
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Initialize draw
                  </>
                )}
              </Button>
            ) : null}
            {isLive && drawModeData?.snapshot ? (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setHistoryOpen(true);
                }}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
              >
                <History className="h-3.5 w-3.5 mr-1.5" />
                View initialization history
              </Button>
            ) : null}
            {bulkActions}
          </div>
        </div>
        {expanded ? (
          <div id={`rodeo-${rodeo.id}-body`}>
            <div className="px-4 pt-3 space-y-3">{expandedPanels}</div>
            <div className="divide-y divide-border">{eventRows}</div>
          </div>
        ) : null}
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => !o && !initialize.isPending && setConfirmOpen(false)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Initialize ferris-wheel rotation?
            </DialogTitle>
            <DialogDescription>
              This rotates every association member through the 4-group
              ferris wheel one time, draws each member a random new
              position inside their new group, and flips the draw to{' '}
              <strong>live</strong> mode (enabling lock, finalize, and
              notifications for this rodeo).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong>This cannot be undone</strong> via the normal admin
              flow. Manual position edits remain possible on the Ferris
              Wheel page.
            </p>
            <p>
              Any sandbox-mode <strong>draft draws will be cleared</strong>{' '}
              so re-runs use the new shifted priorities. Locked or
              finalized draws are never touched.
            </p>
            <p>
              An audit snapshot of every member's prior → new position
              will be saved.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={initialize.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleInitialize} disabled={initialize.isPending}>
              {initialize.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running…
                </>
              ) : (
                'Initialize draw'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InitializationHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        rodeoId={rodeo.id}
      />
    </Fragment>
  );
}

function DrawModeBadge({ mode }: { mode: 'sandbox' | 'live' }) {
  if (mode === 'live') {
    return (
      <Badge
        variant="outline"
        className="border-emerald-400/60 bg-emerald-100/70 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-900/40 dark:text-emerald-100"
      >
        <CheckCircle2 className="mr-1 h-3 w-3" />
        Live
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="border-amber-400/60 bg-amber-100/70 text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/40 dark:text-amber-100"
    >
      <AlertTriangle className="mr-1 h-3 w-3" />
      Sandbox
    </Badge>
  );
}
